// Pure helpers for the "Mapa klientů" page (filtering, counters, formatting).
import { MACHINE_STATUS_LABELS } from '../constants';

// Map groups (backend: models/ClientMap.js STATUS_GROUP). Colours follow MACHINE_STATUS_COLORS.
export const MAP_GROUPS = [
  { value: 'provoz', label: 'V provozu', color: '#388E3C', bg: '#E8F5E9' },
  { value: 'vyroba', label: 'Ve výrobě', color: '#F57C00', bg: '#FFF3E0' },
  { value: 'objednano', label: 'Objednáno', color: '#1976D2', bg: '#E3F2FD' },
];
export const GROUP_META = Object.fromEntries(MAP_GROUPS.map((g) => [g.value, g]));

export const KRAJE = [
  'Hlavní město Praha', 'Středočeský kraj', 'Jihočeský kraj', 'Plzeňský kraj',
  'Karlovarský kraj', 'Ústecký kraj', 'Liberecký kraj', 'Královéhradecký kraj',
  'Pardubický kraj', 'Kraj Vysočina', 'Jihomoravský kraj', 'Olomoucký kraj',
  'Zlínský kraj', 'Moravskoslezský kraj',
];
export const NO_KRAJ = '__none__';

export const CZ_CENTER = [49.8, 15.5];
export const CZ_ZOOM = 7;

const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export const EMPTY_FILTERS = { group: '', model: '', kraj: '', search: '' };

function machineMatches(m, f) {
  return (!f.group || m.group === f.group) && (!f.model || m.model_type === f.model);
}

export function filterClients(clients, f) {
  const q = norm(f.search.trim());
  return clients.filter((c) => {
    if (!c.machines.some((m) => machineMatches(m, f))) return false;
    if (f.kraj === NO_KRAJ ? c.kraj : f.kraj && c.kraj !== f.kraj) return false;
    if (q && ![c.name, c.company_name, c.site_name, c.address.city, c.ico].some((v) => norm(v).includes(q))) return false;
    return true;
  });
}

// Group used for the marker colour: the filtered group if a status filter is active.
export const displayGroup = (client, f) => f.group || client.group;

export function computeCounts(clients, f) {
  const machines = clients.flatMap((c) => c.machines.filter((m) => machineMatches(m, f)));
  const byGroup = Object.fromEntries(MAP_GROUPS.map((g) => [g.value, 0]));
  machines.forEach((m) => { byGroup[m.group] += 1; });
  const byKraj = {};
  clients.forEach((c) => { const k = c.kraj || 'Neurčeno'; byKraj[k] = (byKraj[k] || 0) + 1; });
  return {
    clients: new Set(clients.map((c) => c.id)).size,
    places: clients.length,
    machines: machines.length,
    byGroup,
    byKraj: Object.entries(byKraj).sort((a, b) => b[1] - a[1]),
  };
}

export function modelOptions(clients) {
  return [...new Set(clients.flatMap((c) => c.machines.map((m) => m.model_type)))].sort();
}

export function needsAttention(c) {
  return !c.location || c.geocode.precision === 'city';
}

export function formatDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${Number(d)}. ${Number(m)}. ${y}` : null;
}

export function formatAddress(a) {
  return [a.street, [a.postal_code, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

export function machineDateLabel(m) {
  if (m.group === 'provoz') {
    const d = formatDate(m.delivered_at);
    return d ? `Instalace: ${d}` : 'Instalace: neuvedeno';
  }
  const d = formatDate(m.contract_signed_at);
  return d ? `Smlouva: ${d} · dodání neuvedeno` : 'Termín dodání neuveden';
}

export function modelSummary(machines) {
  const counts = {};
  machines.forEach((m) => { counts[m.model_type] = (counts[m.model_type] || 0) + 1; });
  return Object.entries(counts).map(([model, n]) => (n > 1 ? `${model} × ${n}` : model)).join(', ');
}

export const statusLabel = (status) => MACHINE_STATUS_LABELS[status] || status;

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Only digits and a leading "+" may end up in a tel: link.
export const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, '')}`;

const EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/;
export const isEmail = (v) => EMAIL_RE.test(String(v || '').trim());
